import { asString, download, generateCsv } from "export-to-csv";

import { getCsvConfig } from "~/utils/Helper";

import type { CsvCellValue, CsvExportColumn } from "./types";

/**
 * Pure CSV-building logic for MeqTable's owned export feature. Ported from
 * the pattern ~10 pre-MeqTable call sites already hand-roll (getCsvConfig +
 * generateCsv + download — src/utils/Helper.ts#getCsvConfig wraps
 * export-to-csv's own `mkConfig`), except MeqTable now owns it generically
 * (each cell's resolved value; all columns by default, visible-only when
 * opted in) instead of every call site re-implementing the same few lines.
 *
 * Not a component file — `ExportCsvButton.tsx` (the component) imports these,
 * keeping "component files export components only" intact.
 */

/**
 * Coerces an arbitrary cell value to something `export-to-csv#generateCsv`
 * accepts: nullish -> "" (matches old wrapper call sites' blank-on-empty
 * convention), arrays -> comma-joined (e.g. DISEASE_DESCRIPTIONS — the same
 * transform RawDataTable.tsx/ComparisonTable.tsx apply by hand today),
 * everything else passed through as-is (numbers stay numbers).
 */
export const toCsvCellValue = (value: unknown): CsvCellValue => {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return String(value);
};

const buildCsvConfig = (fileName: string, columns: CsvExportColumn[]) =>
  getCsvConfig(
    fileName,
    columns.map(({ key, displayLabel }) => ({ key, displayLabel })),
  );

/** Pure — returns the CSV text without touching the DOM. Preferred for unit tests. */
export const generateTableCsv = (
  fileName: string,
  columns: CsvExportColumn[],
  rows: Record<string, CsvCellValue>[],
): string => asString(generateCsv(buildCsvConfig(fileName, columns))(rows));

/** Browser-only — builds the CSV and triggers a file download (same `download` fn the old call sites use). */
export const downloadTableCsv = (
  fileName: string,
  columns: CsvExportColumn[],
  rows: Record<string, CsvCellValue>[],
): void => {
  const csvConfig = buildCsvConfig(fileName, columns);
  download(csvConfig)(generateCsv(csvConfig)(rows));
};
